import { useState, useEffect, useCallback } from 'react';
import { Award, Lock, Check, Loader2, Zap, MessageCircle, Crown, Palette, Calendar, Heart, Users, Star, Brain, TrendingUp } from 'lucide-react';
import { Progress } from '../../components/ui/progress';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  'zap': Zap, 'message-circle': MessageCircle, 'award': Award, 'crown': Crown,
  'palette': Palette, 'calendar': Calendar, 'heart': Heart, 'users': Users,
  'users-plus': Users, 'star': Star, 'brain': Brain, 'trending-up': TrendingUp,
};

export default function AchievementsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAchievements = useCallback(() => {
    fetch(`${API}/api/achievements`, { credentials: 'include' })
      .then(r => r.json()).then(setData).catch(() => { /* non-critical */ }).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAchievements(); }, [fetchAchievements]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-8" data-testid="achievements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-space text-2xl md:text-3xl text-white mb-2">Achievements</h1>
          <p className="text-white/55">Unlock badges by using SendPI</p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-space font-bold text-amber-400">{data.earned_count}/{data.total_count}</p>
          <p className="text-white/40 text-xs">{data.completion_percent}% complete</p>
        </div>
      </div>

      <Progress value={data.completion_percent} className="h-2 bg-white/10" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.badges.map(badge => {
          const Icon = ICON_MAP[badge.icon] || Award;
          return (
            <div key={badge.id} data-testid={`badge-${badge.id}`}
              className={`relative rounded-2xl p-5 border transition-all ${
                badge.earned
                  ? 'bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/30 shadow-lg shadow-amber-500/5'
                  : 'bg-[#0E1830] border-white/5 opacity-70'
              }`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  badge.earned
                    ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white'
                    : 'bg-white/5 text-white/20'
                }`}>
                  {badge.earned ? <Icon className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-medium text-sm ${badge.earned ? 'text-white' : 'text-white/50'}`}>{badge.name}</h3>
                    {badge.earned && <Check className="w-4 h-4 text-amber-400" />}
                  </div>
                  <p className="text-white/40 text-xs mt-0.5">{badge.desc}</p>
                  {!badge.earned && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white/30 text-[10px]">{badge.current} / {badge.threshold}</span>
                        <span className="text-white/30 text-[10px]">{badge.progress}%</span>
                      </div>
                      <Progress value={badge.progress} className="h-1.5 bg-white/5" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
